import React, { useContext } from 'react';
import { GlobalContext } from '../../context/Provider';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface TablePaginationProps {
  count: number;
  limit: number;
  page: number;
  data?: any[];
  onPageChange?: (page: number) => void;
  showDetails?: boolean;
  className?: string;
  activeColor?: string;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  count = 0,
  limit = 10,
  page = 1,
  data,
  onPageChange: customPageChange,
  showDetails = true,
  className = '',
  activeColor = '#1AA7DE',
}) => {
  const globalContext = useContext(GlobalContext);

  const totalPages = Math.max(1, Math.ceil(count / limit));
  const currentPage = Math.max(1, Math.min(page, totalPages));

  // Determine visited count / records range
  const currentCount = Array.isArray(data) ? data.length : 0;
  const startRecord = count === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endRecord = currentCount > 0
    ? (currentPage - 1) * limit + currentCount
    : Math.min(currentPage * limit, count);

  const handlePage = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;

    if (customPageChange) {
      customPageChange(newPage);
    } else if (globalContext?.onChangePagination) {
      globalContext.onChangePagination(newPage);
    }
  };

  // Generate page numbers with smart ellipsis: [1] [2] [3] [4] ... [56]
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= 6) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  if (count <= 0) {
    return null;
  }

  return (
    <div
      className={`flex flex-col sm:flex-row items-center justify-between gap-3 text-sm select-none ${className}`}
    >
      {/* Left side: Showing 1 to 10 of 554 results */}
      {showDetails && (
        <div className="text-xs sm:text-sm text-slate-500 font-medium whitespace-nowrap">
          Showing <span className="font-bold text-slate-900">{startRecord}</span> to{' '}
          <span className="font-bold text-slate-900">{endRecord}</span> of{' '}
          <span className="font-bold text-slate-900">{count}</span> results
        </div>
      )}

      {/* Right side: Previous / Numbers / Next */}
      {totalPages > 1 && (
        <div className="flex items-center gap-2 ml-auto">
          {/* Previous Button - Always whitespace-nowrap */}
          <button
            type="button"
            onClick={() => handlePage(currentPage - 1)}
            disabled={currentPage <= 1}
            aria-label="Previous page"
            className="whitespace-nowrap inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 hover:text-[#1AA7DE] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE]"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            <span>Previous</span>
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-1 mx-1">
            {getPageNumbers().map((item, index) => {
              if (item === '...') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="w-7 h-7 flex items-center justify-center text-slate-400 text-xs font-semibold select-none"
                  >
                    ...
                  </span>
                );
              }

              const pageNum = Number(item);
              const isActive = pageNum === currentPage;

              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => handlePage(pageNum)}
                  style={{
                    backgroundColor: isActive ? activeColor : undefined,
                  }}
                  className={`w-7 h-7 flex items-center justify-center text-xs font-bold rounded-lg transition-all ${
                    isActive
                      ? 'text-white shadow-2xs'
                      : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          {/* Next Button - Always whitespace-nowrap */}
          <button
            type="button"
            onClick={() => handlePage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
            className="whitespace-nowrap inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 hover:text-[#1AA7DE] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE]"
          >
            <span>Next</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TablePagination;
